import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { theme } from '../../tokens';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const StyledSkeleton = styled.div`
  background: linear-gradient(
    90deg,
    ${theme.colors.neutral[200]} 25%,
    ${theme.colors.neutral[100]} 50%,
    ${theme.colors.neutral[200]} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${({ $radius }) => $radius || theme.radii.md};

  ${({ $width }) =>
    $width &&
    css`
      width: ${typeof $width === 'number' ? `${$width}px` : $width};
    `}

  ${({ $height }) =>
    $height &&
    css`
      height: ${typeof $height === 'number' ? `${$height}px` : $height};
    `}

  ${({ $variant }) => {
    switch ($variant) {
      case 'text':
        return css`
          height: 1em;
          width: 100%;
        `;
      case 'title':
        return css`
          height: 1.5em;
          width: 60%;
        `;
      case 'avatar':
        return css`
          width: 40px;
          height: 40px;
          border-radius: ${theme.radii.full};
        `;
      case 'thumbnail':
        return css`
          width: 80px;
          height: 80px;
          border-radius: ${theme.radii.lg};
        `;
      case 'image':
        return css`
          width: 100%;
          height: 200px;
          border-radius: ${theme.radii.lg};
        `;
      case 'button':
        return css`
          width: 120px;
          height: 44px;
          border-radius: ${theme.radii.lg};
        `;
      case 'card':
        return css`
          width: 100%;
          height: 200px;
          border-radius: ${theme.radii.xl};
        `;
      default:
        return '';
    }
  }}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${theme.colors.neutral[200]};
  }
`;

const SkeletonGroup = styled.div`
  display: flex;
  flex-direction: ${({ $direction }) => $direction || 'column'};
  gap: ${({ $gap }) => $gap || theme.spacing[2]};
`;

export const Skeleton = ({ variant, width, height, radius, className, ...props }) => {
  return (
    <StyledSkeleton
      $variant={variant}
      $width={width}
      $height={height}
      $radius={radius}
      className={className}
      aria-hidden="true"
      {...props}
    />
  );
};

// Skeleton presets for common use cases
Skeleton.Text = ({ lines = 3, ...props }) => (
  <SkeletonGroup>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        width={i === lines - 1 ? '75%' : '100%'}
        {...props}
      />
    ))}
  </SkeletonGroup>
);

Skeleton.Card = (props) => (
  <SkeletonGroup $gap={theme.spacing[4]}>
    <Skeleton variant="image" {...props} />
    <Skeleton variant="title" />
    <Skeleton.Text lines={2} />
  </SkeletonGroup>
);

Skeleton.ListItem = (props) => (
  <SkeletonGroup $direction="row" $gap={theme.spacing[3]} style={{ alignItems: 'center' }}>
    <Skeleton variant="avatar" {...props} />
    <SkeletonGroup style={{ flex: 1 }}>
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="40%" />
    </SkeletonGroup>
  </SkeletonGroup>
);

Skeleton.Table = ({ rows = 5, cols = 4, ...props }) => (
  <SkeletonGroup $gap={theme.spacing[3]}>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <SkeletonGroup key={rowIndex} $direction="row" $gap={theme.spacing[4]}>
        {Array.from({ length: cols }).map((_, colIndex) => (
          <Skeleton key={colIndex} variant="text" style={{ flex: 1 }} {...props} />
        ))}
      </SkeletonGroup>
    ))}
  </SkeletonGroup>
);

Skeleton.displayName = 'Skeleton';

export default Skeleton;
